import React, {useState} from 'react';
import {useMutation} from '@apollo/client';
import gql from 'graphql-tag';
import {Form, Formik, FormikHelpers, FormikValues} from 'formik';
import * as Yup from 'yup';
import PrimaryButton from '../../../ui/PrimaryButton';
import TextButton from '../../../ui/TextButton';
import {LanguageId} from '../../../models/utils/Languages';
import {FieldConfigurationFragment} from '../../../scenes/app/formConfiguration/fragments';
import {handleError, handleSuccess} from '../../../utils/Apollo';
import {TranslatedWord} from '../../../models/form/FieldConfiguration';
import SectionConfiguration from '../../../models/form/SectionConfiguration';
import ExternalButtonSelector from '../components/ExternalButtonSelector';
import SimpleQuestionTranslations from '../components/SimpleQuestionTranslations';
import LabelLanguageManager from './LabelLanguageManager';
import withTranslations, {InjectedTranslationsProps} from './withTranslations';
import {SectionConfigurationQL} from '../../../models/graphql/SectionConfigurationQL';
import {ActionType} from '../types';
import {useHistory} from 'react-router';

interface Props extends InjectedTranslationsProps {
  defaultLanguage: LanguageId
  configuration?: SectionConfiguration
  onCreateUpdate: (action: ActionType, el?: SectionConfiguration) => void;
}

function SectionForm({
                       defaultLanguage,
                       configuration,
                       onCreateUpdate,
                       translations,
                       onAddTranslation,
                       onDeleteTranslation
                     }: Props) {
  const [createSubmit] = useMutation<{ section: SectionConfigurationQL }>(createMutation);
  const [updateSubmit] = useMutation<{ section: SectionConfigurationQL }>(updateMutation);
  const [loading, setLoading] = useState(false);

  const history = useHistory();
  const manager = new LabelLanguageManager(translations, configuration);

  const onFormSubmit = (values: FormikValues, helpersReact: FormikHelpers<FormikValues>) => {
    setLoading(true);
    const labels: TranslatedWord[] = [];
    Object.entries(values).forEach((value) => {
      const [langId, label] = value;
      if (translations.indexOf(langId) >= 0) {
        labels.push({language: langId, translation: label});
      }
    });
    const submit = configuration ? updateSubmit({
      variables: {data: {names: labels}, type: 'EvaluationFormType', id: configuration.id}
    }) : createSubmit({
      variables: {data: {names: labels}, type: 'EvaluationFormType'}
    });
    setTimeout(() => {
      submit.then(
        (result) => {
          handleSuccess(configuration ? 'Section updated' : 'Section added');
          if (result.data?.section) {
            const newConfiguration = SectionConfiguration.fromQL(result.data?.section);
            onCreateUpdate(configuration ? ActionType.Updated : ActionType.Created, newConfiguration);
          }
        },
        (apolloError) => handleError(apolloError)
      ).finally(() => setLoading(false));
      helpersReact.setSubmitting(false);
    }, 400);
  };

  return (
    <>
      <Formik
        initialValues={manager.initialFormValues()}
        validationSchema={Yup.object(manager.validationObject())}
        onSubmit={onFormSubmit}>
        {({errors}) => (
          <Form className="text-base">
            <div className="pt-3">
              <SimpleQuestionTranslations languages={translations} defaultLanguage={defaultLanguage}
                                          deleteTranslation={onDeleteTranslation} label="Section name" errors={errors}/>
              <ExternalButtonSelector className={'pt-3 -ml-2'} options={manager.availableLanguages()}
                                      addItem={onAddTranslation}
                                      btnTitle={<>Add&nbsp;translation</>}/>
            </div>
            <div className="pt-5 -ml-2">
              <PrimaryButton loading={loading}>Save changes</PrimaryButton>
              <TextButton onClick={(e) => {
                e.preventDefault();
                history.goBack();
              }}>Cancel</TextButton>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default withTranslations(SectionForm);

const createMutation = gql`
  mutation CreateMutation($type: FormType!, $data: SectionInput!)
  {
    section: createSection(type:$type, data:$data) {
      id
      fields {
        ...CompleteFieldConfiguration
      }
      labels: names {
        language
        translation
      }
      sortableFields
      editable
    }
  }
  ${FieldConfigurationFragment}
`;

const updateMutation = gql`
  mutation UpdateMutation($id: ID!, $type: FormType!, $data: SectionInput!) {
    section: updateSection(id:$id, type:$type, data:$data) {
      id
      labels: names {
        language
        translation
      }
      fields {
        ...CompleteFieldConfiguration
      }
      sortableFields
      editable
    }
  }
  ${FieldConfigurationFragment}
`;
